/**
 * Idea here is use this as a mixin with your own UI-component class or object. This gives class keyboard navigability. There are some
 * things to consider when doing things like this. I recommend reading this article: https://developer.mozilla.org/en-US/docs/Web/Accessibility/Keyboard-navigable_JavaScript_widgets
 *
 * These functions implement way to control keyboard navigation inside a UI-component via HTMlElement.focus() function.
 *
 * @example
 *
 * import  {
 *   keyboardNavigable,
 *   nonRollingControlMode,
 * } from '../mixins/keyboard-navigable';
 *
 * class MySearchUIComponentWhatEver {}//Just declare normal class
 * const instance = new MySearchUIComponentWhatEver();//Instantiate your class
 *
 * //And assign functionality to it by extending class prototype like:
 * Object.assign( MySearchUIComponentWhatEver.prototype, keyboardNavigable, nonRollingKeyboardNavigable | overRollingKeyboardNavigable)
 */

/**
 * This navigation control mode stop at first and last element.
 */
const nonRollingControlMode = {

  /**
   * Return previous focused element index
   * @param {HTMLElement} focusedElement current focused element
   * @returns {number} previous focuced element index
   */
  _findNextFocusableElement( focusedElement ) {
    const index = this._findFocusedElementIndex( focusedElement );

    if( index + 1 <= this.focusableElements.length ) {
      return this.focusableElements[ index + 1 ];
    }
  },

  /**
   * Return previous focused element index
   * @param {HTMLElement} focusedElement current focused element
   * @returns {number} previous focuced element index
   */
  _findPreviousFocusableElement(focusedElement) {
    const index = this._findFocusedElementIndex( focusedElement );

    if( index - 1 >= 0 ) {
      return this.focusableElements[ index - 1 ];
    }
  }
}

/**
 * This navigation control mode rolls over. When user presses down button and the component focus is in the last focusable element. The focus is taken to first focusable element.
 * In a sense focus travels round and round and not stopping at the ends of focusable element list.
 */
const rollingControlMode = {
  _findNextFocusableElement( focusedElement ) {
    const index = this._findFocusedElementIndex( focusedElement );

    if( index + 1 >= this.focusableElements.length ) {
      return this.focusableElements[ 0 ];
    } else {
      return this.focusableElements[ index + 1 ];
    }
  },

  /**
   * Find previous focused element
   * @param {HTMLElement} focusedElement current focused element
   * @returns {HTMlElement} previous focudes element
   */
  _findPreviousFocusableElement(focusedElement) {
    const index = this._findFocusedElementIndex( focusedElement );

    if( index - 1 < 0 ) {
      return this.focusableElements[ this.focusableElements.length - 1 ];
    } else {
      return this.focusableElements[ index - 1 ];
    }
  },
}

/**
 * Keyboard navigable component mixin.
 */
const keyboardNavigable = {

  /**
   * Bind event listeners to keyboard navitable element
   * @param {HTMLElement} element - element to bind event listeners
   */
  bindKeyboardNavitable(element) {
    element.addEventListener('keydown', this._onKeyDown.bind(this));
    element.addEventListener('focus', this._onFocus.bind(this));
  },

  /**
   * Update focusable elements by calling this
   * @param {HTMLElements} focusableElements focusable elements
   */
  setFocusaleElements( focusableElements ) {
    this.focusableElements = Array.from( focusableElements );
  },

  /**
   * Find focused element index inside the focucable elements list
   * @param {HTMlElement} focusedElement focused element
   * @returns {number} element index in focucedelements array
   */
  _findFocusedElementIndex( focusedElement ) {
    return this.focusableElements.findIndex( element => element === focusedElement );
  },

  /**
   * Set focus to first focusable element inside instead of the "container" element.
   * @protected
   */
  _onFocus() {
    this.setFocusedElement( this.focusableElements[0] );
    if( typeof this._onFocusIn === 'function' ) {
      this?._onFocusIn(this._focusedElement);
    }
  },

  /**
   * Move focus to a element
   * @public
   * @param {HTMLElement} focusedElement focused element
   */
  setFocusedElement(focusedElement) {
    if( focusedElement ) {
      this._focusedElement = focusedElement;
      this._focusedElement.focus();
      this._focusedElement = focusedElement;
    }
  },

  /**
   * Set keyboard group element, the one element that is `tabindex="0"`-
   * @param {HTMLElement} element group element
   */
  setKeyboarGroupElement( element ) {
    this._groupElement = element;
  },

  /**
   * When a user presses keydown
   * @param {Event} event - Key down event
   * @returns {boolean} false - Stop scrolling user press down or up
   */
  _onKeyDown(event) {
    // Okey this bit is little messy..
    if( event.shiftKey && event.code === 'Tab' ) { // Shift + tab = focus moves out from the element

      // Change "container" element tabindex for a moment to -1 to allow user tab outside of the element
      this._groupElement?.setAttribute('tabindex', '-1');

      setTimeout(() => {
        this._groupElement?.setAttribute('tabindex', '0'); // Allow tabbing back to the element
      },200);

      if( typeof this._tabOut === 'function' ) {
        this._tabOut();
      }

      return false;
    } else if( event.code === 'Tab' && typeof this._tabOut === 'function' ) { // Tabout
      this._tabOut();
    }

    //Up and down
    if( ['ArrowDown','ArrowUp'].includes(event.code) ) {
      event.preventDefault(); // Stop scrolling
      event.stopPropagation(); // Stop scrolling
    }

    if( event.code === 'ArrowDown' ) { // Arrow down
      this.setFocusedElement( this._findNextFocusableElement( this._focusedElement ) );
    }

    if( event.code === 'ArrowUp' ) { // Arrow up
      this.setFocusedElement( this._findPreviousFocusableElement( this._focusedElement ) );
    }

    if( event.code === 'Enter' ) { // Enter
      if (typeof this?._onEnterPressed === 'function') {
        this?._onEnterPressed( this._focusedElement );
      }
    }

    return false;
  }
}

export {
  keyboardNavigable,
  nonRollingControlMode,
  rollingControlMode
}
